import { inject, Injectable } from "@angular/core";
import { HttpClient, HttpErrorResponse, HttpResponse } from "@angular/common/http";
import { Observable } from "rxjs";
import { catchError, tap } from "rxjs/operators";

import { LG_MESSAGE_BUS_SERVICE, LgMessageBusService } from "@logex/framework/lg-application";
import { LgTranslateService } from "@logex/framework/lg-localization";
import { LgPromptDialog } from "@logex/framework/ui-core";

@Injectable({ providedIn: "root" })
export class LgDownloadService {
    protected _httpClient = inject(HttpClient);
    protected _messageBusService = inject(LG_MESSAGE_BUS_SERVICE);
    private _promptDialog = inject(LgPromptDialog);
    private _lgTranslate = inject(LgTranslateService);

    do(url: string, params: any): Observable<HttpResponse<Blob>> {
        return this._httpClient
            .post(url, params, {
                responseType: "blob",
                observe: "response",
                params: {
                    [LgMessageBusService.connectionIdParamName]:
                        this._messageBusService.getConnectionId()!
                }
            })
            .pipe(
                catchError(async error => {
                    this._promptDialog.alert(
                        this._lgTranslate.translate("FW.FAILED_REQUEST_TO_SERVER"),
                        await this._formatServerError(error),
                        {
                            columns: 5,
                            buttons: [LgPromptDialog.CLOSE_BUTTON]
                        }
                    );
                    throw error;
                }),
                tap(data => {
                    if (data.body) {
                        const blob = new Blob([data.body], {
                            // blob default type is an empty string
                            type: data.headers.get("Content-Type") ?? ""
                        });

                        const contentDispositionValue =
                            data.headers.get("Content-Disposition") ??
                            "attachment; filename='filename.*'";
                        const fallbackFilename = /filename="(.*)"/gi
                            .exec(contentDispositionValue)
                            ?.pop();
                        const fileNameUtf8 = /filename\*=UTF-8''(.*)/gi
                            .exec(contentDispositionValue)
                            ?.pop();

                        const fileName =
                            fileNameUtf8 != null
                                ? decodeURIComponent(fileNameUtf8)
                                : fallbackFilename;

                        const link = document.createElement("a");
                        link.href = window.URL.createObjectURL(blob);
                        link.download = fileName ?? "file";

                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                    }
                })
            );
    }

    private async _formatServerError(error: HttpErrorResponse): Promise<string> {
        let errorText = "Error status " + error.status;
        if (error.error != null) {
            const errorObj =
                error.error instanceof Blob
                    ? await this._readErrorResponseBlobAsObj(error.error)
                    : error.error;
            if (errorObj.Message != null) {
                errorText = errorObj.Message;
                if (errorObj.ExceptionMessage != null) {
                    errorText += `<br /><b>${errorObj.ExceptionMessage}</b>`;
                }
                if (errorObj.StackTrace != null) {
                    errorText += `<br /><div class="confirm-message__section confirm-message__scrollable" ><b>Stack:</b><br />
                        ${errorObj.StackTrace.replace(/\n/g, "<br/>")}</div>`;
                }
            } else {
                const find = /<b>\s*Description:\s*<\/b>([^<]*)<br>/.exec(errorObj.data);
                if (find) {
                    errorText = find[1];
                }
            }
        }

        return new Promise(resolve => resolve(errorText));
    }

    private _readErrorResponseBlobAsObj(blob: Blob): Promise<any | null> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(JSON.parse(reader.result as string));
            reader.onerror = reject;
            reader.readAsText(blob);
        });
    }
}
